import {
  AiCard,
  AiLinkCardGroup,
  AiLoadingPanel,
  AiPanel,
  Button,
  Css,
  Tokens,
  useModal,
  useTestIds,
} from "@homebound/beam";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ImportFromDocumentModal } from "src/components/uploader/ImportFromDocumentModal";
import { AiProposalStatus, AssetInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useCreatePlanPackageAiProposalMutation } from "src/routes/libraries/plan-package-v2/components/overview/PlanPackageDocumentImportPanel.generated";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import {
  planPackageElevationsRoute,
  planPackageOptionsRoute,
  planPackageProgramDataRoute,
} from "src/routing/domains/libraries/plan-packages/planPackagesPaths";

/** Which panel we're showing: the CTA, the proposal running, or its findings. */
type ImportState = "idle" | "processing" | "findings";

/** Invites the user to import plan data from their Construction Documents, then reports on the import. */
export function PlanPackageDocumentImportPanel() {
  const params = useRouteParams<PlanPackageDetailsParams>();
  const { planPackageId } = params;
  const { openModal } = useModal();
  const navigate = useNavigate();
  const tid = useTestIds({}, "planPackageDocumentImportPanel");
  const [createProposal] = useCreatePlanPackageAiProposalMutation();
  const [state, setState] = useState<ImportState>("idle");

  // TODO: PLAN-1722 adds the poll jobs that report when the proposal turns COMPLETE. Until then, fake
  // the hand-off from processing to findings on a timer so the flow can be exercised end-to-end.
  // We may end up needing the polling to happen in PlanPackagePageV2
  useEffect(() => {
    if (state !== "processing") return;
    const timer = setTimeout(() => setState("findings"), 5_000);
    return () => clearTimeout(timer);
  }, [state]);

  async function onImport(asset: AssetInput) {
    const { data } = await createProposal({ variables: { input: { planPackageId, asset } } });
    // A proposal that comes back already COMPLETE has nothing to wait on, so skip the processing panel.
    const complete = data?.createPlanPackageAiProposal.status.code === AiProposalStatus.Complete;
    setState(complete ? "findings" : "processing");
  }

  if (state === "processing") {
    return (
      <AiLoadingPanel
        title="We're processing your document..."
        message="If you leave this page the processing will continue and your results will show up here for your review and acceptance. You can overwrite any imported content."
      />
    );
  } else if (state === "findings") {
    // TODO: Build these from the proposal's `entityProposals` once we can read them back — both the
    // counts and which tabs actually changed are hardcoded for now.
    return (
      <AiLinkCardGroup
        cards={[
          {
            title: "Review 3 changed elevations",
            message:
              "Lorem ipsum dolor sit amet consectetur adipiscing elit. Ducimus consequat et et est aliquip nobis culpa occaecat ea quis reprehenderit sunt.",

            action: { onClick: () => navigate(planPackageElevationsRoute.url(params)) },
          },
          {
            title: "Review 5 program data updates",
            message: "Lorem ipsum dolor sit amet consectetur adipiscing elit.",
            action: { onClick: () => navigate(planPackageProgramDataRoute.url(params)) },
          },
          {
            title: "Review 8 changed options",
            message:
              "Lorem ipsum dolor sit amet consectetur adipiscing elit. Ducimus consequat et et est aliquip nobis culpa occaecat ea quis reprehenderit sunt.",
            action: { onClick: () => navigate(planPackageOptionsRoute.url(params)) },
          },
        ]}
      />
    );
  }

  return (
    <AiPanel rounded padding="lg">
      <AiCard size="lg">
        <div css={Css.df.fdc.gap1.px3.py2.$}>
          <span css={Css.lg.aiBoldText.$} {...tid.title}>
            Import Plan Data from your Construction Documents
          </span>
          <span css={Css.sm.color(Tokens.OnSurface).$} {...tid.message}>
            Upload your Construction Documents and AI will extract and fill in Elevations, Locations, Options and
            Program Data to your Plan Package. This process takes about 5 minutes. Then you'll review and accept the
            results.
          </span>
          <div css={Css.pt2.$}>
            <Button
              label="Upload Document"
              icon="upload"
              onClick={() =>
                openModal({
                  size: "lg",
                  aiMode: true,
                  drawHeaderBorder: true,
                  content: <ImportFromDocumentModal onImport={onImport} />,
                })
              }
            />
          </div>
        </div>
      </AiCard>
    </AiPanel>
  );
}
